import React, { useCallback } from 'react';

import { Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

interface HubSectionHeaderProps {
  title: string;
  showControls?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  /** Optional right-side control, e.g. a `HubFilterDropdown`. */
  action?: React.ReactNode;
}

// Atoms has no IconButton; a layout primitive with `onClick` renders a real
// <button> (requires aria-label) styled as the small 24px control from the
// Figma mobile spec.
const CAROUSEL_CONTROL_CLASS =
  'h-6 w-6 cursor-pointer items-center justify-center rounded bg-od-bg-tertiary text-od-content-primary transition-colors hover:bg-od-bg-hover-tertiary';

export const HubSectionHeader: React.FC<HubSectionHeaderProps> = ({
  title,
  showControls = false,
  onPrev,
  onNext,
  action,
}) => {
  const handlePrev = useCallback(() => onPrev?.(), [onPrev]);
  const handleNext = useCallback(() => onNext?.(), [onNext]);

  return (
    <Row className="w-full items-center justify-between gap-4">
      <Text as="h2" className="text-subheader-xs text-od-content-primary">
        {title}
      </Text>
      {action}
      {showControls && (
        <Row className="items-center gap-2">
          <Row
            onClick={handlePrev}
            aria-label="Previous"
            analyticsName="hub-carousel-previous"
            className={CAROUSEL_CONTROL_CLASS}
          >
            <IconChevronLeft size={20} />
          </Row>
          <Row
            onClick={handleNext}
            aria-label="Next"
            analyticsName="hub-carousel-next"
            className={CAROUSEL_CONTROL_CLASS}
          >
            <IconChevronRight size={20} />
          </Row>
        </Row>
      )}
    </Row>
  );
};
